// BIP-39 Recovery-Wortliste + QR-Code-Export
// Die Wortliste CODIERT DAS MASTER-PASSWORT NICHT direkt —
// sie ist ein Hint/Backup-Mechanismus für den Nutzer.
// Technisch: Wir speichern einen verschlüsselten Recovery-Blob.

import * as bip39 from 'bip39';
import QRCode from 'qrcode';
import { deriveKey, encrypt, decrypt, uint8ToBase64, base64ToUint8 } from './crypto';
import { db } from '../db/schema';

const RECOVERY_STORE_KEY = 'recovery-blob';
const RECOVERY_SALT_KEY  = 'recovery-salt';

// ── Wortliste erzeugen ─────────────────────────────────────────────────────

/** Erzeugt 24 zufällige BIP-39-Wörter (256 Bit Entropie). */
export function generateMnemonic(): string {
  return bip39.generateMnemonic(256); // 24 Wörter
}

/** Validiert eine Wortliste. */
export function validateMnemonic(mnemonic: string): boolean {
  return bip39.validateMnemonic(mnemonic);
}

// ── Recovery-Blob speichern ────────────────────────────────────────────────

/**
 * Verschlüsselt das Master-Passwort mit dem Recovery-Mnemonic
 * und speichert den Blob in IndexedDB.
 * So kann das Passwort aus der Wortliste wiederhergestellt werden.
 */
export async function storeRecoveryBlob(
  masterPassword: string,
  mnemonic: string,
): Promise<void> {
  const recoveryEntropy = await bip39.mnemonicToSeed(mnemonic);
  const recoverySalt    = recoveryEntropy.slice(0, 32);
  const recoveryKey     = await deriveKey(
    mnemonic.split(' ').join(''), // Mnemonic als "Passwort"
    new Uint8Array(recoverySalt),
  );
  const encrypted = await encrypt(
    new TextEncoder().encode(masterPassword),
    recoveryKey,
  );

  await db.cryptoMeta.bulkPut([
    { id: RECOVERY_BLOB_KEY, value: encrypted },
    { id: RECOVERY_SALT_KEY, value: new Uint8Array(recoverySalt) },
  ]);
}

const RECOVERY_BLOB_KEY = RECOVERY_STORE_KEY;

// ── Recovery durchführen ───────────────────────────────────────────────────

/**
 * Stellt das Master-Passwort aus der Wortliste wieder her.
 * Gibt das Klartext-Passwort zurück (nur kurz im Speicher!).
 */
export async function recoverPasswordFromMnemonic(mnemonic: string): Promise<string> {
  if (!validateMnemonic(mnemonic)) {
    throw new Error('Ungültige Recovery-Wortliste — bitte alle 24 Wörter prüfen.');
  }

  const blobRecord = await db.cryptoMeta.get(RECOVERY_BLOB_KEY);
  if (!blobRecord) throw new Error('Kein Recovery-Blob gefunden. Setup wurde nicht abgeschlossen.');

  const recoveryEntropy = await bip39.mnemonicToSeed(mnemonic);
  const recoverySalt    = new Uint8Array(recoveryEntropy.slice(0, 32));
  const recoveryKey     = await deriveKey(
    mnemonic.split(' ').join(''),
    recoverySalt,
  );

  const plainBytes = await decrypt(blobRecord.value, recoveryKey);
  return new TextDecoder().decode(plainBytes);
}

// ── QR-Code-Export ─────────────────────────────────────────────────────────

/**
 * Exportiert die Recovery-Wortliste als QR-Code (Data-URL).
 * Für Ausdruck auf Papier — sicher verwahren!
 */
export async function mnemonicToQRDataURL(mnemonic: string): Promise<string> {
  return QRCode.toDataURL(mnemonic, {
    errorCorrectionLevel: 'H', // Höchste Fehlerkorrektur
    margin: 2,
    width: 400,
    color: {
      dark: '#000000',
      light: '#FFFFFF',
    },
  });
}

/**
 * Gibt die Wortliste als downloadbare Textdatei zurück.
 */
export function downloadMnemonicTxt(mnemonic: string): void {
  const content = [
    '=== AMed App — Recovery-Wortliste ===',
    `Datum: ${new Date().toLocaleDateString('de-AT')}`,
    `Nutzer: Dr. med. Klaus Karrer, MSc.`,
    '',
    'WICHTIG: Diese Liste sicher aufbewahren!',
    'Bei Verlust sind alle Daten unwiederbringlich verloren.',
    '',
    ...mnemonic.split(' ').map((w, i) => `${String(i + 1).padStart(2, ' ')}. ${w}`),
    '',
    '=== Ende der Recovery-Wortliste ===',
  ].join('\n');

  const blob = new Blob([content], { type: 'text/plain;charset=utf-8' });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement('a');
  a.href     = url;
  a.download = `amed-recovery-${new Date().toISOString().slice(0, 10)}.txt`;
  a.click();
  URL.revokeObjectURL(url);
}

// Hilfsfunktion für Tests
export { uint8ToBase64, base64ToUint8 };
